import * as React from 'react'

import { observer } from 'mobx-react'

import { PermitFieldType } from '~/client/graph'
import formFieldsCaptionMap from '~/client/src/desktop/constants/formFieldsCaptionMap'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import StruxhubTextArea from '~/client/src/shared/components/StruxhubInputs/StruxhubTextArea'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Keys from '~/client/src/shared/enums/Keys'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import TypeSelector from '../TypeSelector'
import BaseInstructionsConfiguratorStore, {
  BaseInstructions,
} from './BaseInstructionsConfigurator.store'

interface IProps<T> {
  className?: string
  instructions: BaseInstructions<T>
  onChange: (instructions: BaseInstructions<T>) => void
  store: BaseInstructionsConfiguratorStore<T>
  instructionItemRenderer: (item: T, index: number) => JSX.Element
  fieldType: PermitFieldType
  singleModeElement?: JSX.Element
  fieldStateElement?: JSX.Element
}

// translated

@observer
export default class BaseInstructionsConfigurator<T> extends React.Component<
  IProps<T>
> {
  public componentDidUpdate(prevProps: Readonly<IProps<T>>) {
    const { instructions, store, onChange } = this.props
    if (instructions !== prevProps.instructions) {
      store.setInstructions(instructions)
    }
    if (onChange !== prevProps.onChange) {
      store.setOnChangeHandler(onChange)
    }
  }

  public render() {
    const {
      store,
      className,
      instructionItemRenderer,
      singleModeElement,
      fieldStateElement,
      fieldType,
    } = this.props

    const {
      instructions: { list, listType },
      removeInstruction,
      shouldShowRemovingConfirmDialog,
      hideRemovingConfirmModal,
      listTypeAsOptions,
    } = store

    return (
      <>
        <ConfirmDialog
          doneTitle={Localization.translator.delete}
          useRedConfirmationButton
          title={Localization.translator.deleteItem}
          onDoneClicked={removeInstruction}
          isOpen={shouldShowRemovingConfirmDialog}
          onCancelClicked={hideRemovingConfirmModal}
        >
          {Localization.translator.areYouSureWantToDeleteInstructionItem}
        </ConfirmDialog>

        <div
          className={`tw:flex tw:flex-col tw:flex-1 tw:mt-3 tw:[&_.instruction-item_select]:tw:min-w-[190px] tw:[&_.instruction-item_.del-icon]:tw:right-0 ${
            className || ''
          }`}
        >
          <div className="tw:flex tw:items-center tw:gap-2.5 bg-palette-brand-lightest tw:rounded-[4px] ba-light-input-border tw:px-[15px] tw:mb-[15px]">
            <TooltipWrapper
              content={formFieldsCaptionMap[fieldType]}
              className="tw:flex tw:items-center tw:grow-0"
            >
              <PermitFieldIcon fieldName={fieldType} />
            </TooltipWrapper>
            {singleModeElement ? (
              singleModeElement
            ) : (
              <>
                <span className="tw:text-title-md tw:text-foreground tw:grow-0 tw:whitespace-nowrap">
                  {Localization.translator.typeOfList}
                </span>
                <TypeSelector
                  selectedValue={listType}
                  options={listTypeAsOptions}
                  onChange={this.handleListTypeChange}
                />
              </>
            )}
            {fieldStateElement && (
              <div className="tw:ml-auto">{fieldStateElement}</div>
            )}
          </div>

          <div className="tw:ml-5">
            <div>{list.map(instructionItemRenderer)}</div>
            {!singleModeElement && this.renderAdditor()}
          </div>
        </div>
      </>
    )
  }

  private renderAdditor() {
    const {
      showTextarea,
      newInstructionText,
      addNewInstruction,
      shouldShowTextarea,
    } = this.props.store

    return shouldShowTextarea ? (
      <StruxhubTextArea
        isMinimalisticMode={true}
        placeholder={`${Localization.translator.enterInstruction}...`}
        value={newInstructionText}
        onBlur={addNewInstruction}
        onChange={this.handleTextareaChange}
        onKeyPress={this.handleTextareaKeyPress}
        autoFocus={true}
      />
    ) : (
      <div
        className="tw:text-title-sm tw:text-primary tw:cursor-pointer"
        onClick={showTextarea}
      >
        {Localization.translator.addAdditionalItem}
      </div>
    )
  }

  private handleListTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.props.store.changeListType(event.target.value)
  }

  private handleTextareaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    this.props.store.changeNewInstruction(event.target.value)
  }

  private handleTextareaKeyPress = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === Keys.Enter && !event.shiftKey) {
      this.props.store.addNewInstruction()
    }
  }
}
